import {
	Design,
	DesignText,
	ProductTemplate,
	RosterPartType,
	Upload,
	ValidPreviewWidth,
} from '../../common/data/designs.js';
import { Id, Maybe, RosterEntry } from '../../common/data.js';
import { keepExisting, lit, maybe } from '../../common/util.js';
import { defaultRosterEntry } from '../../common/data/roster.js';
import base62 from '@fry/base62';
import { Product, ProductImage } from '../../common/data/products.js';
import { ColorTuple, MainColorGroup, mainColorGroups, rgbToHex, stringifyColorId } from '../../common/data/colors.js';

let contentUrlPrefix = '';

export const setContentUrlPrefix = (url: string) => contentUrlPrefix = url || '';
export const getContentUrlPrefix = () => contentUrlPrefix;

export const getWebpUrl = (src: string) => contentUrlPrefix + (src.match(/^.(design-preview|upload-preview)/) ? src.replace(/.png$/, '.webp') : `/webp${src}.webp`);
export const wrapImageUrl = getWebpUrl;

export function getUploadPreviewURL(upload: Upload, profile = 'rgb') {
	return `${contentUrlPrefix}/upload-preview/${upload.date}/${upload.id}.${profile}.webp`;
}
export function getBaseUploadPreviewURL(upload: Upload) {
	return `/upload-preview/${upload.date}/${upload.id}.rgb.png`;
}

/**
 * Parameters that alter the URL generated (and image eventually returned) from makeDesignPreviewUrl.
 * If the design ID is passed as the first argument, all of these override the respective parts of the design.
 * Otherwise, a blank design with the default template for the product is used.
 * Not all combinations of these are guaranteed to make sense, or even render.
 */
export interface DesignPreviewImageParams {
	
	/**
	 * ID of the `Product` to replace the actual product used in the design.
	 * A correct replacement `productTemplate` ID is required alongside.
	 */
	product?: Maybe<Id<Product>>;
	
	/**
	 * ID of `ProductTemplate` to replace what is used in the design.
	 * If it's a customizable ('commands') template, colors from the design are applied.
	 * If used without any design, a blank design with default colors will be generated.
	 */
	productTemplate?: Maybe<Id<ProductTemplate>>;
	
	/**
	 * Side of the product to render. That's currently 'front' or 'back', but technically there can be more.
	 * Null by default, meaning all sides in a row.
	 */
	side?: Maybe<string>;
	
	/**
	 * Width of a preview of each side, from one of the allowed values.
	 */
	previewWidth?: Maybe<ValidPreviewWidth>;
	
	/**
	 * Width of the whole preview image.
	 * If `side` is given, this is equal to `previewWidth` (since there's just one preview).
	 * If this is set, `previewWidth` is ignored.
	 */
	fullWidth?: Maybe<ValidPreviewWidth>;
	
	/**
	 * Output format and file extension for the link. `webp` by default.
	 */
	format?: Maybe<'png' | 'webp' | 'jpeg'>;
	
	/**
	 * A `RosterEntry` object used to replace text/country on roster-customizable design parts (name, number, alias, flag).
	 */
	rosterEntry?: Maybe<RosterEntry>;
	
	/**
	 * ID of the `Upload` to put into the generated design. Ignored if design given and already contains logo.
	 */
	upload?: Maybe<Id<Upload>>;
	
	/**
	 * A map of color overrides. Strings can be colorId's (with @) or hex values (without #)
	 */
	colors?: Maybe<{ [K in MainColorGroup]?: Maybe<ColorTuple> }>;
	
	/**
	 * Add customizable parts.
	 */
	addParts?: Maybe<RosterPartType[]>,
	
	/**
	 * Text config. Only font handled at the moment.
	 */
	textConfig?: Maybe<DesignText>;
	
}

export const previewUrlRosterPartKeys = lit([['name', 'rn'], ['number', 'r0'], ['alias', 'ra'], ['flag', 'rf']]);
export const previewUrlAddPartKeys = lit([['name', 'an'], ['number', 'a0'], ['alias', 'aa'], ['flag', 'af']]);

export type DesignPreviewKey = (
	'n' // no design id
	| 'p' // product id
	| 'ps' // side
	| 'fw' | 'pw' // fullWidth or previewWidth
	| 'pt' // productTemplate
	| (typeof previewUrlRosterPartKeys)[number][1] | 'r' // ra used to be r due to typo
	| (typeof previewUrlAddPartKeys)[number][1] | 'az' // add all valid
	| 'tf' // font
	| 'u' // upload
	| `c${number}` // main colors
);

/**
 * Link to a design preview generated on demand.
 * All of the entity parameters are IDs (since this just puts them in the URL).
 * The second argument is optional extra params that are applied *on top* of the design passed, see interface docs above.
 */
export const makeDesignPreviewUrl = (designId: Maybe<Id<Design>>, {
	product,
	side,
	previewWidth = 384,
	fullWidth,
	format = 'webp',
	productTemplate,
	rosterEntry,
	upload,
	colors,
	addParts = [],
	textConfig,
}: DesignPreviewImageParams = {}) => {
	
	const opts: Maybe<DesignPreviewKey | `${DesignPreviewKey}-${string}`>[] = [
		
		product ? `p-${product}` : null,
		side ? `ps-${side}` : null,
		fullWidth ? `fw-${fullWidth}` : `pw-${previewWidth}`,
		
		productTemplate ? `pt-${productTemplate}` : null,
		...!rosterEntry ? [] : previewUrlRosterPartKeys.map(([key, abbr]) => (
			maybe(rosterEntry[key], val => val != defaultRosterEntry[key] ? lit(`${abbr}-${base62.encode(val)}`) : null)
		)),
		...addParts ? previewUrlAddPartKeys.map(([key, abbr]) => (addParts.includes(key) ? abbr : null)) : designId ? [] : lit(['az']),
		
		textConfig?.font ? `tf-${textConfig.font}` : null,
		
		upload ? `u-${upload}` : null,
		
		...colors ? mainColorGroups.map((cid, i) => maybe(
			colors[cid],
			val => lit(`c${i}-${typeof val == 'string' ? val : val.length < 10 ? rgbToHex(val).slice(1) : stringifyColorId(val)}`),
		)) : [],
		
	];
	
	const parts = [designId ?? 'n', ...keepExisting(opts), format];

	return `/design-preview/${parts.join('.')}`;
	
};

export const getProductPreviewImageUrl = (image: ProductImage, width: ValidPreviewWidth = 512, designPreviewParams: DesignPreviewImageParams = { previewWidth: width, side: 'front' }) => {
	if (image.image) return `/data/products/${image.image.replace(/(\.\w+)$/, `.${width}$1`)}`;
	if (image.design) return makeDesignPreviewUrl(image.design, designPreviewParams);
	if (image.path) return width < 512 ? image.path.replace(/\.[a-zA-Z]+$/, '.thumb.jpeg') : image.path;
	return null;
};
